import { atom } from "jotai";
import { Quote } from "../types/quotes";
import { getAccessToken } from "../services/amplify";

import {
  updateItemsInShoppingCartAtom,
  shoppingCartAtomV2,
} from "./shopping-cart-v2";
import { atomWithRefresh, loadable } from "jotai/utils";
import { Pagination } from "../types/pagination";
import { QuoteStatuses } from "../constants/quotes";

import { atomEffect } from "jotai-effect";
import { PAGE_SIZE } from "./pagination";
import { showToast } from "../components/toast";
import { trackEvent } from "../services/posthog";
import { PostHogEvent } from "../constants/posthog";
import { uniqueId } from "lodash";

export const loadingGetQuotesAtom = atom(false);

const getQuotesEffect = atomEffect((get, set) => {
  if (get(loadableGetQuotesAtom).state === "loading") {
    set(loadingGetQuotesAtom, true);
  } else {
    set(loadingGetQuotesAtom, false);
  }
});

export const createQuoteEffect = atomEffect((get, set) => {
  if (get(loadableAsyncRequestResponseCreatedQuoteAtom).state === "loading") {
    set(loadingGetQuotesAtom, true);
  } else {
    set(loadingGetQuotesAtom, false);
  }

  if (get(loadableAsyncRequestResponseCreatedQuoteAtom).state === "hasData") {
    set(getQuotesAtom);
  }
});

export const loadingQuotesPendingForReviewAtom = atom(false);

const quotesPendingForReviewAtom = atomEffect((get, set) => {
  if (get(loadableQuotesPendingForReviewAtom).state === "loading") {
    set(loadingQuotesPendingForReviewAtom, true);
  } else {
    set(loadingQuotesPendingForReviewAtom, false);
  }
});

export const currentQuoteAtom = atom<Quote>({
  id: "",
  name: `Quote #${Math.floor(Math.random() * 1000000)}`,
  tags: [],
  dateCreated: "",
  products: [],
  client: null,
  author: null,
  status: QuoteStatuses.DRAFT,
});

export const currentStepIndexAtom = atom(0);

export const updateCurrentQuoteAtom = atom(null, (get, set, update: Quote) => {
  set(currentQuoteAtom, update);
});

export const resetCurrentQuoteAtom = atom(null, (get, set) => {
  set(currentQuoteAtom, {
    id: "",
    name: `Quote #${Math.floor(Math.random() * 1000000)}`,
    tags: [],
    dateCreated: "",
    products: [],
    client: null,
    author: null,
    status: QuoteStatuses.DRAFT,
  });
  set(currentStepIndexAtom, 0);
});

const BASE_URL = `${process.env.REACT_APP_API_PRODUCTS}/internal/v3/quotes/`;

export const currentQuotePageAtom = atom<string>(BASE_URL);

export const goToPaginationPageAtom = atom(null, (get, set, page: number) => {
  const url = `${BASE_URL}?page=${page}&page_size=${PAGE_SIZE}`;
  set(currentQuotePageAtom, url);
});

const parseFilters = (url: string, filters: string[], search: string) => {
  const queryParams = new URLSearchParams();

  if (search) {
    queryParams.set("search", search);
  }

  if (filters.length) {
    filters.forEach((filter) => {
      queryParams.append("statuses", filter);
    });
  }
  if (queryParams.toString() === "") {
    return "";
  }

  if (url.includes("?")) {
    return `&${queryParams}`;
  } else {
    return `?${queryParams}`;
  }
};

export const quotePaginationAtom = atom<Pagination<null>>((get) => {
  get(getQuotesEffect);
  const getQuoteLoadable = get(loadableGetQuotesAtom);

  if (getQuoteLoadable.state !== "hasData") {
    return {
      metadata: {
        total: NaN,
        total_pages: NaN,
        first_page: NaN,
        last_page: NaN,
        page: 1,
        previous_page: NaN,
        next_page: NaN,
      },
      links: {
        self: `${process.env.REACT_APP_API_PRODUCTS}/internal/v3/quotes/`,
        first: "",
        last: "",
        prev: "",
        next: "",
      },
      data: null,
    };
  } else {
    const paginationLinks = getQuoteLoadable.data.links;
    const metadata = getQuoteLoadable.data.metadata;

    return {
      metadata: {
        total: metadata.total,
        total_pages: metadata.total_pages,
        first_page: metadata.first_page,
        last_page: metadata.last_page,
        page: metadata.page,
        previous_page: NaN,
        next_page: NaN,
      },
      links: {
        self: paginationLinks.self as string,
        first: paginationLinks.first,
        last: paginationLinks.last,
        prev: paginationLinks.prev,
        next: paginationLinks.next,
      },
      data: null,
    };
  }
});

export const asyncRequestResponseCreatedQuoteAtom = atom<Promise<any> | null>(
  null,
);

export const loadableAsyncRequestResponseCreatedQuoteAtom = loadable(
  asyncRequestResponseCreatedQuoteAtom,
);

export const loadingCreateQuoteAtom = atom(false);

export const createQuoteAtom = atom(null, async (get, set) => {
  const currentQuote = get(currentQuoteAtom);
  const shoppingCart = get(shoppingCartAtomV2);
  const url = `${process.env.REACT_APP_API_PRODUCTS}/internal/v3/quotes/`;
  const accessToken = await getAccessToken();

  const payload: { [key: string]: any } = {
    name: currentQuote.name,
  };

  if (currentQuote.client?.id) {
    payload.client_information_id = currentQuote.client?.id;
  }

  payload.quote_details = shoppingCart.map((item, index) => ({
    product_price_id: item.productPriceId,
    quantity: item.quantity,
    client_price: item.sellingPrice,
    display_order: index,
  }));

  set(loadingCreateQuoteAtom, true);
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error();
    }
    trackEvent(PostHogEvent.QUOTE_COMPLETE, {});
    showToast({
      title: "Quote Created",
      description: "Quote has been created successfully",
      status: "success",
    });
    set(asyncRequestResponseCreatedQuoteAtom, response.json());
    set(loadingCreateQuoteAtom, false);
  } catch (error) {
    showToast({
      title: "Error",
      description: "Failed to create quote",
      status: "error",
    });
    set(loadingCreateQuoteAtom, false);
  }
});

export const updateQuoteAtom = atom(null, async (get, set) => {
  const currentQuote = get(currentQuoteAtom);

  const accessToken = await getAccessToken();

  const shoppingCart = get(shoppingCartAtomV2);

  const updateQuotesPayload = {
    name: currentQuote.name,
    client_information_id: currentQuote.client?.id,
    status: currentQuote.status,
    quote_details: shoppingCart.map((item, index) => ({
      product_price_id: item.productPriceId,
      quantity: item.quantity,
      display_order: index,
      client_price: item.sellingPrice,
    })),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_PRODUCTS}/internal/v3/quotes/${currentQuote.id}/`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updateQuotesPayload),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to update quote");
    }
    set(getQuotesAtom);
    showToast({
      title: "Quote Updated",
      description: "Quote has been updated successfully",
      status: "success",
    });
  } catch (error) {
    showToast({
      title: "Quote Update Failed",
      description: "Failed to update quote",
      status: "error",
    });
  }
});

export const quotesAtom = atom<Quote[]>((get) => {
  get(getQuotesEffect);
  const getQuoteLoadable = get(loadableGetQuotesAtom);
  if (
    getQuoteLoadable.state === "loading" ||
    getQuoteLoadable.state === "hasError"
  ) {
    return [];
  }
  const rawQuotes = getQuoteLoadable.data?.data || [];
  return rawQuotes.map((rawQuote: any) => ({
    id: rawQuote.id,
    name: rawQuote.name,
    tags: [],
    status: rawQuote.status,
    dateCreated: rawQuote.date_created,
    products: rawQuote.quote_details.map((rawProduct: any) => ({
      quoteDetailId: rawProduct.id,
      productPriceId: rawProduct.product_price_id,
      seats: rawProduct.quantity,
      cost: rawProduct.cost,
      msrp: rawProduct.msrp,
      interval: rawProduct.interval,
      productName: rawProduct.product.friendly_name,
      clientPrice: rawProduct.client_price,
      itemRetailValue: rawProduct.product.max_device_value_usd || 0,
      serviceFee: rawProduct.product.deductible_schedules?.[0]?.deductible || 0,
      deviceType: {
        key: rawProduct.product.covered_item_specifications?.[0]?.item_type.id,
        label:
          rawProduct.product.covered_item_specifications?.[0]?.item_type.name,
      },
      subDeviceType: rawProduct.product.covered_item_specifications?.[0]
        ?.item_group?.id
        ? {
            key: rawProduct.product.covered_item_specifications[0].item_group
              .id,
            label:
              rawProduct.product.covered_item_specifications[0].item_group.name,
          }
        : null,
      coverageTypes: rawProduct.product.covered_loss_type_categories.map(
        (item: string) => item.replace("Malfunctions", "Mechanical Breakdown"),
      ),
      displayOrder: rawProduct.display_order || 0,
    })),
    client: rawQuote.client_information
      ? {
          id: rawQuote.client_information.id,
          contracts: 0,
          companyName: rawQuote.client_information.company_name,
          corporateName: rawQuote.client_information.corporate_name,
          domainNameUrl: rawQuote.client_information.domain_name_url,
          firstName: rawQuote.client_information.first_name,
          lastName: rawQuote.client_information.last_name,
          primaryContactEmail:
            rawQuote.client_information.primary_contact_email,
          resellerId: rawQuote.client_information.reseller_id,
        }
      : null,
    author: {
      id: rawQuote.author.id,
      email: rawQuote.author.email,
      first_name: rawQuote.author.first_name,
      last_name: rawQuote.author.last_name,
    },
  }));
});

export const getQuotesAtom = atomWithRefresh<Promise<any>>(async (get) => {
  const url: string = get(currentQuotePageAtom);
  const filters = parseFilters(
    url,
    get(selectedFiltersAtom),
    get(stringFilterAtom),
  );

  const accessToken = await getAccessToken();

  const response = await fetch(`${url}${filters}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch quotes");
  }

  return await response.json();
});

export const loadableGetQuotesAtom = loadable(getQuotesAtom);

export const selectedFiltersAtom = atom<string[]>([]);

export const stringFilterAtom = atom<string>("");

export const convertQuoteToPolicyAtom = atom(
  null,
  async (_get, _set, quoteId: string) => {
    const url = `${process.env.REACT_APP_API_PRODUCTS}/internal/v3/convert-quote/${quoteId}/`;
    const accessToken = await getAccessToken();

    return await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
  },
);

export const convertQuoteStateAtom = atom(
  null,
  async (
    _get,
    _set,
    quoteId: string,
    quote: {
      name: string;
      status: QuoteStatuses.DRAFT | QuoteStatuses.PENDING;
      client_information_id: string;
    },
  ) => {
    const url = `${process.env.REACT_APP_API_PRODUCTS}/internal/v3/quotes/${quoteId}/`;
    const accessToken = await getAccessToken();

    return await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(quote),
    });
  },
);

export const getQuotesPendingForReviewAtom = atomWithRefresh(async (_get) => {
  const url = `${BASE_URL}?page=1&page_size=1&statuses=${QuoteStatuses.PENDING}`;

  const accessToken = await getAccessToken();

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch quotes");
  }

  return await response.json();
});

export const loadableQuotesPendingForReviewAtom = loadable(
  getQuotesPendingForReviewAtom,
);

export const hasQuotesPendingForReviewAtom = atom<boolean>((get) => {
  get(quotesPendingForReviewAtom);
  const loadable = get(loadableQuotesPendingForReviewAtom);

  if (loadable.state === "loading" || loadable.state === "hasError") {
    return false;
  }

  return loadable.data.data.length > 0;
});

export const loadableConvertQuoteToPolicyAtom = loadable(
  convertQuoteToPolicyAtom,
);

export const setQuoteToEditAtom = atom(
  null,
  (
    get,
    set,
    args: {
      quote: Quote;
      step?: number;
    },
  ) => {
    set(currentStepIndexAtom, args.step || 0);
    set(updateCurrentQuoteAtom, args.quote);
    set(updateItemsInShoppingCartAtom, {
      items: args.quote.products
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((product) => ({
          key: uniqueId(),
          quantity: product.seats,
          sellingPrice: product.clientPrice,
          deviceType: product.deviceType,
          subDeviceType: product.subDeviceType,
          productPriceId: product.productPriceId,
          coverageTypes: product.coverageTypes,
          interval: product.interval,
          serviceFee: product.serviceFee,
          msrp: product.msrp,
          value: product.cost,
          deviceValue: product.itemRetailValue,
          isSellingPriceDirty: true,
        })),
    });
  },
);
